import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { fromEvent, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { AuditService } from './services/audit.service';
import { AuthService } from './services/auth.service';
import { ThemeService } from './core/theme/theme.service';
import { BrandingService } from './core/branding/branding.service';
import { readCachedStudentTheme } from './core/theme/student-theme-tokens-applier';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Driver Program - Kane County Bar Association';
  private readonly destroy$ = new Subject<void>();
  private restoreInFlight = false;
  private restoreSub: Subscription | null = null;
  contentVisible = true;

  constructor(
    private audit: AuditService,
    private themeService: ThemeService,
    private brandingService: BrandingService,
    private authService: AuthService,
    private router: Router,
    private translate: TranslateService
  ) { }

  ngOnInit(): void {
    // Start audit capture (navigation + click tracking)
    this.audit.init();

    const cached = readCachedStudentTheme();
    if (cached) {
      this.themeService.applyCachedLogos(cached.siteLogoUrl, cached.homeLetterLogoUrl);
    }
    void this.themeService.loadAndApply();
    void this.brandingService.loadAndApply();

    // Tab title must remain English (no language switching).
    this.brandingService.branding$
      .pipe(takeUntil(this.destroy$))
      .subscribe((raw) => {
        const title = raw.programTitleEn?.trim();
        if (title) {
          document.title = title;
        }
        // Re-render translated text so {{programName}} picks up the latest branding.
        const lang = this.translate.getCurrentLang();
        if (lang) {
          this.translate.setTranslation(lang, {}, true);
        }
      });

    // On return to the app, recover the session only when the access token is
    // actually expired. Valid access tokens should not trigger refresh/reload.
    fromEvent(document, 'visibilitychange')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (document.visibilityState !== 'visible') {
          return;
        }
        this.restoreSessionIfExpired();
      });

    fromEvent(window, 'focus')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.restoreSessionIfExpired());

    fromEvent(window, 'pageshow')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event: Event) => {
        const pageEvent = event as PageTransitionEvent;
        if (pageEvent.persisted) {
          this.restoreSessionIfExpired();
        }
      });
  }

  ngOnDestroy(): void {
    this.restoreSub?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private restoreSessionIfExpired(): void {
    // Public pages / guests: never force a "session invalid" login redirect.
    if (
      this.router.url.includes('/login') ||
      this.router.url.includes('/register') ||
      this.router.url.includes('/forgot-password') ||
      this.router.url.includes('/reset-password') ||
      this.router.url.includes('/password-recovery')
    ) {
      return;
    }

    // No stored session → do nothing (home/courses/about/contact are public).
    if (!this.authService.isAuthenticated()) {
      return;
    }

    const accessExpired =
      this.authService.isTokenExpired() || !this.authService.getToken();
    if (!accessExpired || this.restoreInFlight) {
      return;
    }

    this.restoreInFlight = true;
    this.restoreSub?.unsubscribe();
    this.restoreSub = this.authService.ensureValidSession().subscribe({
      next: (ok) => {
        this.restoreInFlight = false;
        if (!ok) {
          this.router.navigate(['/login'], { queryParams: { reason: 'session' } });
          return;
        }

        // Only remount authenticated areas so public pages are not disrupted.
        if (
          this.router.url.startsWith('/dashboard') ||
          this.router.url.startsWith('/profile')
        ) {
          this.remountCurrentPage();
        }
      },
      error: () => {
        this.restoreInFlight = false;
        this.router.navigate(['/login'], { queryParams: { reason: 'session' } });
      },
    });
  }

  private remountCurrentPage(): void {
    this.contentVisible = false;
    setTimeout(() => {
      this.contentVisible = true;
    }, 0);
  }
}
